import { Charge, ChargeDetails, ChargePlan, ChargePlanData, ContactSalesData } from '../models/paywall-settings';
import { richTextOrEmpty } from './rich-text.helper';

export const CHARGE_MODEL_FLAT = 0;
export const CHARGE_MODEL_QUANTITY = 1;
export const UNBOUNDED_SEAT_MAX = 9999;
const DEFAULT_PLACEHOLDER_TO_QUANTITY = 1;
const MAX_TIER_SENTINEL = 1_000_000_000;

export function isQuantityCharge(charge: Charge | undefined): boolean {
  return charge?.model === CHARGE_MODEL_QUANTITY || charge?.model === 'Quantity' as never;
}

export function quantityCharges(plan: ChargePlan | undefined): Charge[] {
  return (plan?.charges ?? []).filter(isQuantityCharge);
}

/** A plan takes a seat count when any of its charges is quantity based; that count drives every such charge. */
export function isQuantityChargePlan(plan: ChargePlan | undefined): boolean {
  return quantityCharges(plan).length > 0;
}

/**
 * Card copy is stored per charge plan. A plan without its own entry has no card copy: the admin shows it as
 * "No card text", so it must not borrow another card's copy.
 */
export function chargePlanDataForPlan(
  plan: ChargePlan | undefined,
  data: ChargePlanData[] | null | undefined
): ChargePlanData | undefined {
  return plan ? data?.find(item => item.chargePlanId === plan.id) : undefined;
}

export function isContactSalesEnabled(contactSalesData?: ContactSalesData | null): boolean {
  return !!contactSalesData?.enabled;
}

export function contactSalesTitle(contactSalesData?: ContactSalesData | null): string {
  return contactSalesData?.title || 'Enterprise';
}

export function contactSalesSlaNote(contactSalesData?: ContactSalesData | null): string {
  return contactSalesData?.slaNote || 'A sales engineer replies within one business day.';
}

export function contactSalesIncludesHtml(contactSalesData?: ContactSalesData | null): string {
  return richTextOrEmpty((contactSalesData?.includesSubTitle ?? []).filter(part => !!part).join(''));
}

/**
 * Seat bounds come from the quantity based charges only, in the given currency when one is set. With several
 * quantity charges the selectable range is where all of them have a price: the highest minimum and the
 * lowest bounded maximum.
 */
export function getSeatBounds(
  plan: ChargePlan | undefined,
  chargePlanData?: ChargePlanData | null,
  currencyCode?: string
): {
  min: number;
  max: number;
  step: number;
  initial: number;
} {
  const charges = quantityCharges(plan);
  let min = 1;
  let max = UNBOUNDED_SEAT_MAX;
  for (const charge of charges) {
    const bounds = chargeSeatBounds(charge.chargeDetails ?? [], currencyCode);
    min = Math.max(min, bounds.min);
    max = Math.min(max, bounds.max);
  }
  if (max < min) {
    max = min;
  }
  const configuredStep = chargePlanData?.quantityStep ?? 1;
  const step = configuredStep > 1 ? configuredStep : 1;
  const defaultQty = charges[0]?.defaultQuantity ?? min;
  const unclamped = defaultQty >= min ? defaultQty : min;
  const initial = Math.min(max, Math.max(min, unclamped));
  return { min, max, step, initial };
}

function chargeSeatBounds(allDetails: ChargeDetails[], currencyCode?: string): { min: number; max: number } {
  const inCurrency = currencyCode
    ? allDetails.filter(detail => detail.priceCurrencyCode === currencyCode)
    : allDetails;
  const details = inCurrency.length > 0 ? inCurrency : allDetails;
  const sorted = [...details].sort((a, b) => (a.fromQuantity ?? 0) - (b.fromQuantity ?? 0));
  const firstFrom = sorted[0]?.fromQuantity ?? 0;
  const lastTo = Math.max(...sorted.map(detail => detail.toQuantity ?? 0), 0);
  const min = firstFrom > 0 ? firstFrom : 1;
  const isDefaultPlaceholder = sorted.length > 0
    && sorted.every(detail => (detail.fromQuantity ?? 0) <= 0 && (detail.toQuantity ?? 0) <= DEFAULT_PLACEHOLDER_TO_QUANTITY);
  const lastIsUnbounded = lastTo <= 0 || lastTo >= MAX_TIER_SENTINEL;
  const max = (!sorted.length || isDefaultPlaceholder || lastIsUnbounded)
    ? UNBOUNDED_SEAT_MAX
    : lastTo;
  return { min, max };
}

/** "1 seat", "2 seats". */
export function seatCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'seat' : 'seats'}`;
}

export function seatsHelperLabel(min: number, max: number, step: number): string {
  const unbounded = max >= UNBOUNDED_SEAT_MAX;
  if (!unbounded && step > 1) {
    return `seats (${min}-${max}, steps of ${step})`;
  }
  if (!unbounded) {
    return `seats (${min}-${max})`;
  }
  if (step > 1) {
    return `seats (${min}+, steps of ${step})`;
  }
  return `seats (${min}+)`;
}

export function periodNoun(billingPeriod: number): string {
  switch (billingPeriod) {
    case 1: return 'quarter';
    case 2: return 'year';
    default: return 'month';
  }
}
